import React from "react";
import { Box, Divider, Grid, Typography, Chip } from "@mui/material";
import {
  ReceiptOutlined,
  LocalShippingOutlined,
  StoreOutlined,
  AttachMoneyOutlined,
  DateRangeOutlined,
  DescriptionOutlined,
  PaymentOutlined,
  InfoOutlined,
  CalendarTodayOutlined,
  PersonOutlined,
  BusinessOutlined,
} from "@mui/icons-material";

import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { localizeDateTimeShort } from "utils/dateFormats";
import { useShipmentRefundDetailContext } from "contexts/PaymentContexts/ShipmentRefundDetailContext";

const statusStyles = {
  Approved: { backgroundColor: "#EBF5FB", textColor: "green" },
  Pending: { backgroundColor: "#FEF9E7", textColor: "#D4AC0D" },
  Rejected: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  default: { backgroundColor: "#E9ECEF", textColor: "#6C757D" },
};

const getStatusStyle = (status) =>
  statusStyles[status] || statusStyles.default;

const ShipmentRefundBasicDetail = () => {
  const { shipmentRefundDetail, loadingShipmentRefund } =
    useShipmentRefundDetailContext();
  const shipmentTotalCharge = Array.isArray(shipmentRefundDetail?.shipmentPrice)
    ? shipmentRefundDetail.shipmentPrice.reduce(
      (sum, price) => sum + (Number(price?.charge) || 0),
      0,
    )
    : 0;
  const refundTypeName =
    shipmentRefundDetail?.refundType?.name || shipmentRefundDetail?.refundType;
  const isAmountOnlyRefund = [
    "Overpayment",
    "Compensation For Loss/ Damage",
  ].includes(refundTypeName);
  const originalCharge = isAmountOnlyRefund
    ? Number(
        shipmentRefundDetail?.originalChargeAmount ||
          shipmentTotalCharge ||
          0,
      )
    : shipmentTotalCharge;

  return !loadingShipmentRefund ? (
    <>
      {/* ── Main Refund Info ─────────────────────────────── */}
      <Box padding={3}>
        <Grid container spacing={4}>
          <Grid item xs={3}>
            <InformationTile
              icon={() => <ReceiptOutlined color="primary" />}
              title="Refund ID"
              subtitle={shipmentRefundDetail?.refundId || "N/A"}
            />
          </Grid>

          <Grid item xs={3}>
            <InformationTile
              icon={() => <LocalShippingOutlined color="primary" />}
              title="Shipment"
              subtitle={shipmentRefundDetail?.shipment?.shipmentId || "N/A"}
            />
          </Grid>

          {shipmentRefundDetail?.vendor && (
            <Grid item xs={3}>
              <InformationTile
                icon={() => <StoreOutlined color="primary" />}
                title="Vendor"
                subtitle={shipmentRefundDetail?.vendor?.ownerName || "N/A"}
              />
            </Grid>)}
          {shipmentRefundDetail?.customer && (
            <Grid item xs={3}>
              <InformationTile
                icon={() => <PersonOutlined color="primary" />}
                title="Customer"
                subtitle={shipmentRefundDetail?.customer?.name || "N/A"}
              />
            </Grid>)}

          <Grid item xs={3}>
            <InformationTile
              icon={() => <AttachMoneyOutlined color="primary" />}
              title="Shipment total charge"
              subtitle={`Rs. ${originalCharge.toFixed(2)}`}
            />
          </Grid>

          <Grid item xs={3}>
            <InformationTile
              icon={() => <AttachMoneyOutlined color="primary" />}
              title="Refund Amount"
              subtitle={
                shipmentRefundDetail?.amount != null
                  ? `Rs. ${shipmentRefundDetail.amount.toFixed(2)}`
                  : "N/A"
              }
            />
          </Grid>

          {isAmountOnlyRefund && (
            <Grid item xs={3}>
              <InformationTile
                icon={() => <AttachMoneyOutlined color="primary" />}
                title="Original Charge"
                subtitle={`Rs. ${Number(
                  originalCharge
                ).toFixed(2)}`}
              />
            </Grid>
          )}

          <Grid item xs={3}>
            <InformationTile
              icon={() => <InfoOutlined color="primary" />}
              title="Refund Type"
              subtitle={shipmentRefundDetail?.refundType?.name || "N/A"}
            />
          </Grid>



          <Grid item xs={3}>
            <InformationTile
              icon={() => <DateRangeOutlined color="primary" />}
              title="Refund Date"
              subtitle={
                shipmentRefundDetail?.refundDate
                  ? localizeDateTimeShort(shipmentRefundDetail.refundDate)
                  : "N/A"
              }
            />
          </Grid>

          <Grid item xs={3}>
            <InformationTile
              icon={() => <InfoOutlined color="primary" />}
              title="Status"
              subtitle={
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="start"
                  mt={1}
                >
                  <Chip
                    label={shipmentRefundDetail?.refundStatus || "Unknown"}
                    style={{
                      backgroundColor: getStatusStyle(
                        shipmentRefundDetail?.refundStatus
                      ).backgroundColor,
                      color: getStatusStyle(shipmentRefundDetail?.refundStatus)
                        .textColor,
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      height: "20px",
                      padding: "0 6px",
                      borderRadius: "12px",
                      marginTop: "4px",
                      width: "auto",
                    }}
                  />
                </Box>
              }
            />
          </Grid>

          {/* COD Transfer specific tiles */}
          {shipmentRefundDetail?.isTransferToCOD && (
            <>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <AttachMoneyOutlined color="primary" />}
                  title="Original Charge"
                  subtitle={`Rs. ${Number(
                    shipmentRefundDetail?.originalChargeAmount || 0
                  ).toFixed(2)}`}
                />
              </Grid>

              <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="COD Price Status"
                  subtitle={
                    shipmentRefundDetail?.codTransferStatus || "Pending Approval"
                  }
                />
              </Grid>
            </>
          )}

          {shipmentRefundDetail?.adjustmentMessage && (
            <Grid item xs={12} sm={6}>
              <InformationTile
                icon={() => <DescriptionOutlined color="primary" />}
                title="COD Adjustment Message"
                subtitle={shipmentRefundDetail.adjustmentMessage}
              />
            </Grid>
          )}

          {shipmentRefundDetail?.refundReason && (
            <Grid item xs={12} sm={6}>
              <InformationTile
                icon={() => <DescriptionOutlined color="primary" />}
                title="Refund Reason"
                subtitle={shipmentRefundDetail.refundReason}
              />
            </Grid>
          )}

          {shipmentRefundDetail?.rejectReason && (
            <Grid item xs={12} sm={6}>
              <InformationTile
                icon={() => <DescriptionOutlined color="error" />}
                title="Rejected from finance"
                subtitle={shipmentRefundDetail.rejectReason}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {/* ── Finance Review Section (only shown when reviewed) ── */}
      {shipmentRefundDetail?.financeReview?.status && (
        <>
          <Divider />
          <Typography variant="h6" padding={2}>
            Refund Detail
          </Typography>
          <Box mt="4" padding={3}>
            <Grid container spacing={4}>

              {/* Approved By / Rejected By */}
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <PersonOutlined color="primary" />}
                  title={
                    shipmentRefundDetail.financeReview.status === "Approved"
                      ? "Approved By"
                      : "Rejected By"
                  }
                  subtitle={
                    shipmentRefundDetail.financeReview.status === "Approved"
                      ? shipmentRefundDetail.financeReview?.approvedBy?.name || "N/A"
                      : shipmentRefundDetail.financeReview?.rejectedBy?.name || "N/A"
                  }
                />
              </Grid>

              {/* Payment Method — only for Approved */}
              {shipmentRefundDetail.financeReview.status === "Approved" && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <PaymentOutlined color="primary" />}
                    title="Payment Method"
                    subtitle={
                      shipmentRefundDetail.financeReview?.paymentMethod?.name ||
                      "N/A"
                    }
                  />
                </Grid>
              )}

              {/* Bank Name — only if present */}
              {shipmentRefundDetail.financeReview?.bankName?.name && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <BusinessOutlined color="primary" />}
                    title="Bank Name"
                    subtitle={
                      shipmentRefundDetail.financeReview.bankName.name
                    }
                  />
                </Grid>
              )}

              {/* Payment Date — only for Approved */}
              {shipmentRefundDetail.financeReview.status === "Approved" && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <CalendarTodayOutlined color="primary" />}
                    title="Payment Date"
                    subtitle={
                      localizeDateTimeShort(
                        shipmentRefundDetail.financeReview?.paymentDate
                      ) || "N/A"
                    }
                  />
                </Grid>
              )}

              {/* Remarks */}
              {shipmentRefundDetail.financeReview?.remarks && (
                <Grid item xs={6}>
                  <InformationTile
                    icon={() => <DescriptionOutlined color="primary" />}
                    title="Remarks"
                    subtitle={shipmentRefundDetail.financeReview.remarks}
                  />
                </Grid>
              )}

              {/* Refund Method */}
              {shipmentRefundDetail.financeReview?.refundMethod && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <PaymentOutlined color="primary" />}
                    title="Refund Method"
                    subtitle={shipmentRefundDetail.financeReview.refundMethod}
                  />
                </Grid>
              )}

              {/* Bank Account */}
              {shipmentRefundDetail.financeReview?.bankAccount && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <BusinessOutlined color="primary" />}
                    title="Bank Account"
                    subtitle={shipmentRefundDetail.financeReview.bankAccount}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      )}
    </>
  ) : (
    <InfoGridSkeleton />
  );
};

export default ShipmentRefundBasicDetail;
